import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { debounce, email, form, FormField, required } from '@angular/forms/signals';
import { BaseDialogComponent } from '../../../../kit/base-dialog/base-dialog.component';
import { routeParams } from '../../../../../modules/shared/shared.global-service';
import { MicrosoftGraphKeysSettingDialogStore } from './microsoft-graph-keys-setting-dialog.store';
import {
    MicrosoftGraphKeysSettingDialogData,
    MicrosoftGraphKeysSettingFormModel,
} from './microsoft-graph-keys-setting-dialog.types';
import { TextInputComponent } from '../../../../kit/input/text-input-v2/text-input.component';
import { EmailInputComponent } from '../../../../kit/input/text-input-v2/email-input.component';
import { DateInputComponent } from '../../../../kit/input/date-input-v2/date-input.component';
import { ActionButtonsComponent } from '../../../../kit/action-buttons/action-buttons.component';
import {
    MicrosoftGraphKeysSettingCreateInput,
} from '@libs/types/src/inbox/input-type/microsoft-graph-keys-setting.create-input';
import {
    MicrosoftGraphKeysSettingUpdateInput,
} from '@libs/types/src/inbox/input-type/microsoft-graph-keys-setting.update-input';
import { successCreated, successUpdated } from '../../../../../modules/content/content.error';
import { dateify, toDateOnly } from '../../../../../modules/shared/helpers/date.helper';
import { LinkButtonComponent } from '../../../../kit/button/link-button.component';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { microsoftGraphKeysSettingDuplicitValidationFrag } from './microsoft-graph-keys-setting-dialog.query';
import { InfoFloatMessageComponent } from '../../../../kit/float-message/info-float-message.component';
import { LocalDatePipe } from '@libs/shared/pipes/local-date.pipe';
import { MicrosoftGraphKeysSettingObject } from '@libs/types/src/inbox/object-type/microsoft-graph-keys-setting.object';
import config from '../../../../../config/config';
import { SessionService } from '../../../../../services/session.service';
import { formatDate } from '@angular/common';
import { ErrorFloatMessageComponent } from '../../../../kit/float-message/error-float-message.component';
import { WarnFloatMessageComponent } from '../../../../kit/float-message/warn-float-message.component';
import {
    MicrosoftGraphKeysSettingResultObject,
} from '@libs/types/src/inbox/object-type/microsoft-graph-keys-setting-result.object';

@Component({
    selector: 'app-microsoft-graph-keys-setting-dialog',
    templateUrl: './microsoft-graph-keys-setting-dialog.component.html',
    styleUrls: ['./microsoft-graph-keys-setting-dialog.component.scss'],
    providers: [MicrosoftGraphKeysSettingDialogStore],
    imports: [
        FormField,
        TextInputComponent,
        EmailInputComponent,
        DateInputComponent,
        ActionButtonsComponent,
        LinkButtonComponent,
        InfoFloatMessageComponent,
        LocalDatePipe,
        ErrorFloatMessageComponent,
        WarnFloatMessageComponent,
    ],
})
export class MicrosoftGraphKeysSettingDialogComponent extends BaseDialogComponent {
    protected readonly store = inject(MicrosoftGraphKeysSettingDialogStore);
    protected readonly data = this.dialogConfig.data as MicrosoftGraphKeysSettingDialogData;
    protected readonly item = this.data.item;
    protected readonly prefix = 'organization-settings.emailClientSettings.';
    private readonly session = inject(SessionService);
    private readonly organizationId = routeParams.organizationId;

    protected readonly formData = signal<MicrosoftGraphKeysSettingFormModel>({
        dateTo: dateify(this.item?.dateTo),
        sendReminderToEmail: this.item?.sendReminderToEmail ?? this.store.globalGlobalStore.loggedUserStore.currentUser()?.email ?? '',
        domain: this.item?.domain ?? '',
        tenantId: this.item?.tenantId ?? '',
        clientId: this.item?.clientId ?? '',
        clientSecret: '',
    });
    protected readonly formDef = form(this.formData, schema => {
        required(schema.domain);
        required(schema.tenantId);
        required(schema.clientId);
        required(schema.clientSecret, {
            when: () => !this.item || this.editClientSecret(),
        });
        email(schema.sendReminderToEmail, {
            when: () => !!this.formData().dateTo,
        });
        debounce(schema.domain, config.debounceTime);
        debounce(schema.dateTo, config.debounceTime);
    });

    protected domainDebounce = this.formDef.domain().value;

    protected editClientSecret = signal<boolean>(false);
    protected loading = signal<boolean>(false);

    protected settingAlreadyExists = signal<boolean>(false);
    protected settingWillInvalidateOthers = signal<boolean>(false);
    protected settingValidToAlreadyExists = signal<Date | null>(null);
    protected settingWillBeValidFrom = signal<Date | null>(null);
    protected enteredKeysDontWork = signal<boolean>(false);
    protected isAnyValidationError = computed(() => {
        return this.settingAlreadyExists() || this.settingValidToAlreadyExists() !== null || this.enteredKeysDontWork()
    });

    private existingSettingsForThisDomain = signal<MicrosoftGraphKeysSettingObject[]>([]);

    constructor() {
        super();
        effect(() => {
            this.formDef.tenantId().value();
            this.formDef.clientId().value();
            this.formDef.clientSecret().value();

            untracked(() => {
                this.enteredKeysDontWork.set(false);
            });
        });
        effect(() => {
            this.formDef.domain().controlValue();

            untracked(() => {
                this.existingSettingsForThisDomain.set([]);
                this.resetValidationVariables()
            });
        });
        effect(() => {
            const domain = this.domainDebounce();

            untracked(() => {
                this.loadExistingSettingsForThisDomain(domain).then()
            });
        });
        effect(() => {
            const existingSettingsForThisDomain = this.existingSettingsForThisDomain();
            const dateTo = this.formData().dateTo;

            untracked(() => {
                this.validateDomain(existingSettingsForThisDomain, dateTo);
            });
        });
    }

    public override async save(): Promise<void> {
        if (this.formDef().invalid() || this.loading() || this.isAnyValidationError()) return;

        const value = this.formData();
        const commonData = {
            dateTo: toDateOnly(value.dateTo),
            sendReminderToEmail: value.dateTo ? value.sendReminderToEmail.trim() || null : null,
            domain: value.domain.trim(),
            tenantId: value.tenantId.trim(),
            clientId: value.clientId.trim(),
            clientSecret: value.clientSecret,
        };

        this.loading.set(true);
        let result: MicrosoftGraphKeysSettingResultObject;
        if (this.item) {
            const input: MicrosoftGraphKeysSettingUpdateInput = {
                ...commonData,
                id: this.item.id,
                clientSecret: this.editClientSecret() ? commonData.clientSecret : undefined,
            };
            result = await this.store.api.updateMicrosoftGraphKeysSetting(input);
        } else {
            const organizationId = Number(this.organizationId());
            if (!organizationId) {
                this.loading.set(false);
                return;
            }

            const input: MicrosoftGraphKeysSettingCreateInput = {
                ...commonData,
                organizationId,
            };
            result = await this.store.api.createMicrosoftGraphKeysSetting(input);
        }
        this.loading.set(false);

        if (!result?.ok) {
            if (result && !result.keysWork) {
                this.enteredKeysDontWork.set(true);
            }
            return;
        }

        if (this.item) {
            successUpdated();
        } else {
            successCreated();
        }

        this.dialogRef.close(true);
    }

    private async loadExistingSettingsForThisDomain(domain: string) {
        if (!domain) {
            this.resetValidationVariables();
            return;
        }

        this.loading.set(true);
        const result = await this.store.api.getMicrosoftGraphKeysSettings(
            this.getFiltersForDomainValidation(domain),
            microsoftGraphKeysSettingDuplicitValidationFrag,
        );
        this.loading.set(false);

        this.existingSettingsForThisDomain.set(result?.items || []);
    }

    private validateDomain(items: MicrosoftGraphKeysSettingObject[], dateTo: Date | null) {
        if (this.loading()) return;

        this.resetValidationVariables();

        if (!items.length) {
            // no error
            return;
        }

        if (items.some((item) => !item.dateTo)) {
            // error
            this.settingAlreadyExists.set(true);
        } else if (dateTo === null) {
            // warning
            this.settingWillInvalidateOthers.set(true);
        } else if (items.some((item) => this.areDatesTheSameDay(new Date(item.dateTo!), dateTo))) {
            // error
            this.settingValidToAlreadyExists.set(dateTo);
        } else {
            const today = new Date();
            const setting = items.find((item) => {
                return new Date(item.dateTo!) < dateTo;
            });
            if (
                setting
                && (
                    this.areDatesTheSameDay(new Date(setting.dateTo!), today)
                    || new Date(setting.dateTo!) > today
                )
            ) {
                // info
                const dateToPlusOneDay = new Date(setting.dateTo!);
                dateToPlusOneDay.setDate(dateToPlusOneDay.getDate() + 1);
                this.settingWillBeValidFrom.set(dateToPlusOneDay);
            }
        }
    }

    private getFiltersForDomainValidation(domain: string) {
        const filter = new FilterBuilder();
        filter.where('organizationId', this.organizationId());
        filter.where('domain', domain);
        if (this.item?.id) {
            filter.where('id', this.item.id, 'NEQ');
        }
        filter.sort('dateTo', 'DESC');
        filter.limit(50);
        return filter.filter();
    }

    private resetValidationVariables() {
        this.settingAlreadyExists.set(false);
        this.settingWillInvalidateOthers.set(false);
        this.settingValidToAlreadyExists.set(null);
        this.settingWillBeValidFrom.set(null);
    }

    private areDatesTheSameDay(date1: Date, date2: Date): boolean {
        return formatDate(date1, 'd.M.y', this.session.locale)
            === formatDate(date2, 'd.M.y', this.session.locale);
    }
}
